/* ==========================================================================
   <lsa-video-plate> · Late Sunday Afternoon
   --------------------------------------------------------------------------
   One media plate that plays from a Shopify-hosted video, YouTube or Vimeo,
   with no cover image required from the merchant.

   Rules it enforces, from the LSA Wearers brief:
     - Muted by default, always. Sound only ever arrives after a click.
     - One plate plays at a time. The nearest to the viewport centre wins.
     - Mobile never autoplays. Poster plus a play affordance instead.
     - prefers-reduced-motion holds the poster and never autoplays.
   ========================================================================== */
if (!customElements.get('lsa-video-plate')) {
  customElements.define(
    'lsa-video-plate',
    class LsaVideoPlate extends HTMLElement {
      connectedCallback() {
        if (this._wired) return;
        this._wired = true;

        this.behavior = this.dataset.behavior || 'plate';
        this.video = this.querySelector('video');
        this.playBtn = this.querySelector('[data-lsa-vp-play]');

        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.smallScreen = window.matchMedia('(max-width: 749px)').matches;
        this.mayAutoplay = !this.reduced && !this.smallScreen;

        if (this.playBtn) {
          this.playBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.openWithSound();
          });
        }

        // Vimeo has no derivable still, so its background player stands in for
        // the poster. Everything else waits until it is actually near the view.
        if (this.dataset.eagerEmbed === 'true' && this.mayAutoplay) {
          this.observe(() => this.injectEmbed(this.dataset.embedAmbient, true));
          return;
        }

        if (this.mayAutoplay) this.observe(() => this.startAmbient());
      }

      // Stays observed for the life of the plate: leaving the viewport has to
      // pause the loop, or every plate scrolled past keeps playing off-screen.
      observe(onEnter) {
        if (!('IntersectionObserver' in window)) return;
        this.io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                onEnter();
              } else {
                this.pauseAmbient();
              }
            });
          },
          { rootMargin: '100px', threshold: 0.25 }
        );
        this.io.observe(this);
      }

      // Only the large sitting plates take the exclusive floor. Strip cells are
      // ambient texture and are meant to run side by side, so claiming there
      // would blank every cell but the last one to scroll in.
      startAmbient() {
        if (this.soundOpen) return;
        if (this.behavior === 'plate') LsaVideoPlate.claim(this);

        if (this.video) {
          this.video.muted = true;
          this.video.preload = 'auto';
          const p = this.video.play();
          if (p && p.catch) p.catch(() => {});
          return;
        }
        if (this.dataset.embedAmbient && !this.iframe) {
          this.injectEmbed(this.dataset.embedAmbient, true);
        }
      }

      pauseAmbient() {
        if (this.soundOpen) return;
        if (this.video && !this.video.paused) this.video.pause();
      }

      openWithSound() {
        this.soundOpen = true;
        LsaVideoPlate.claim(this);
        this.classList.add('lsa-vp--playing');

        if (this.video) {
          this.video.muted = false;
          this.video.loop = false;
          this.video.controls = true;
          const p = this.video.play();
          if (p && p.catch) p.catch(() => {});
        } else if (this.dataset.embedSound) {
          this.injectEmbed(this.dataset.embedSound, false);
        }

        if (this.playBtn) this.playBtn.hidden = true;
      }

      injectEmbed(src, ambient) {
        if (!src) return;
        if (this.iframe) {
          this.iframe.src = src;
          this.classList.toggle('lsa-vp--ambient-embed', !!ambient);
          return;
        }
        const frame = document.createElement('iframe');
        frame.src = src;
        frame.className = 'lsa-vp__media lsa-vp__media--embed';
        frame.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
        frame.setAttribute('allowfullscreen', '');
        frame.setAttribute('loading', 'lazy');
        frame.setAttribute('title', this.getAttribute('data-title') || 'Video');
        if (ambient) frame.setAttribute('tabindex', '-1');
        this.appendChild(frame);
        this.iframe = frame;
        this.classList.add('lsa-vp--has-embed');
        this.classList.toggle('lsa-vp--ambient-embed', !!ambient);
      }

      stopForOther() {
        if (this.soundOpen) {
          this.soundOpen = false;
          this.classList.remove('lsa-vp--playing');
          if (this.playBtn) this.playBtn.hidden = false;
          if (this.video) {
            this.video.muted = true;
            this.video.controls = false;
            this.video.pause();
          }
        }
        if (this.iframe) {
          this.iframe.remove();
          this.iframe = null;
          this.classList.remove('lsa-vp--has-embed', 'lsa-vp--ambient-embed');
        } else if (this.video) {
          this.video.pause();
        }
      }

      /* Only one plate holds the floor at a time. */
      static claim(plate) {
        if (LsaVideoPlate.current && LsaVideoPlate.current !== plate) {
          LsaVideoPlate.current.stopForOther();
        }
        LsaVideoPlate.current = plate;
      }
    }
  );
}
